import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListZones } from 'src/features/zone/api/useListZones';
import { SpaceFormSchemaValues } from 'src/features/spaces/utils/formSchema';
import { disallowedVisitorTranslationMap, spaceTypeTranslationMap } from 'src/features/spaces/utils/constants';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useParking } from 'src/providers/ParkingProvider';

type SpaceFormProps = {
    form: UseFormReturn<SpaceFormSchemaValues>;
    onSubmit: (values: SpaceFormSchemaValues) => void;
};

function SpaceForm({ onSubmit, form }: SpaceFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zonesData } = useListZones({ parkingId });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<SpaceFormSchemaValues>
                form={form}
                name="number"
                label={formatMessage(spacesMessages.number)}
            />
            <FormField
                control={form.control}
                name="zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...spacesMessages.zone} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(spacesMessages.zone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {zonesData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...spacesMessages.type} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(spacesMessages.type)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(spaceTypeTranslationMap).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {formatMessage(value)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="disallow_usage_for"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...spacesMessages.disallowedVisitorCategories} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(spacesMessages.disallowedVisitorCategories)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(disallowedVisitorTranslationMap).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {formatMessage(value)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default SpaceForm;
